import { Bell01, BellOff01, ChevronDown, Copy06, File05, Lightning01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getDeliveryDefaults, IDeliveryPayload } from 'src/app/Dashboard/notificationDelivery';
import NotificationSettingsModal from 'src/app/Dashboard/NotificationSettingsModal';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import ActivityDropdownList, { IActivityDisplayItem } from 'src/components/V3DropdownCmp/ActivityDropdownList';
import CarrierDetailTooltip from 'src/components/V3DropdownCmp/CarrierDetailTooltip';
import useActivityList from 'src/hooks/useActivityList';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppSelector } from 'src/redux/hooks';
import { AppDispatch, RootState } from 'src/redux/store';
import { INotificationTypes } from 'src/services/CustomerService';
import { logError } from 'src/services/LoggingService';
import { saveOrderNotificationSettings, updateOrderPriority } from 'src/services/OrderService';
import { mapActivitiesToDisplayItems } from 'src/utils/activityFeed.helpers';
import { copyToClipboard, extractMcNumber, formatAddress } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { getScheduleColumnValues } from '../Orders/schedule.helpers';

import { TRACK_CARRIER_TRACKING_ID_NAMES } from './track.const';
import { ITrackOrder, updateTrackOrder } from './track.slice';
import { getBadgeType, getOrderTypeBadge, isCheckCommonCarrier } from './track.utils';

interface IProps {
    data: ITrackOrder;
    activeTab: string;
    headCells?: any[];
    selectedTrackIds?: number[];
    onToggleSelect?: (id: number) => void;
    actionColumnWidthClass?: string;
}

const TrackTableRow = ({ data, headCells, selectedTrackIds, onToggleSelect, actionColumnWidthClass }: IProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const currentUser = useAppSelector(currentUserSelector);
    const orgBranding = useOrgBranding();
    const carrierName = data.carrier?.name ?? '';
    // Org presented as the carrier — masked by visibility (BE sends the org name) or the legacy
    // hardcoded name. The tooltip then shows the org identity (logo + MC number) and hides
    // carrier-type/tracking rows, which would otherwise leak details about the hidden carrier.
    const isOrgCarrier = !!data.carrier && (carrierName === orgBranding.name || carrierName === 'Western Alliance');
    const [isFromTooltipOpen, setIsFromTooltipOpen] = useState(false);
    const [isToTooltipOpen, setIsToTooltipOpen] = useState(false);
    const [isCarrierTooltipOpen, setIsCarrierTooltipOpen] = useState(false);
    const [isLatestActivityTooltipOpen, setIsLatestActivityTooltipOpen] = useState(false);

    // Feature gate: resolved 3-tier visibility comes from the listRadarOrders API response → track.portalVisibility
    const isTextNotificationFeatureVisible = useSelector((state: RootState) => state.track.portalVisibility?.textNotification !== false);

    const [textNotificationData, setTextNotificationData] = useState<any>(data?.textNotification ?? null);
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

    // Pre-fill: order-wise settings first; fall back to user's global notification settings.
    // Mirrors the exact pattern in OrderView/index.tsx.
    const notificationExistingData = useMemo(() => {
        const orderWise = (textNotificationData as any)?.notificationTypes?.notificationTypes;
        if (orderWise) return orderWise;

        return currentUser?.notificationSettings?.notificationTypes ?? null;
    }, [textNotificationData, currentUser?.notificationSettings]);

    const isNotificationEnabled = useMemo(() => {
        const notifData = textNotificationData as any;

        if (notifData) {
            // Order-level notificationTypes JSONB
            if (notifData.notificationTypes?.notificationTypes) {
                return Object.values(notifData.notificationTypes.notificationTypes).some((entry: any) => entry?.enabled === true);
            }
        }

        // Fall back to global notification settings when no order-level data
        const globalTypes = currentUser?.notificationSettings?.notificationTypes;

        return globalTypes ? Object.values(globalTypes).some((entry: any) => entry?.enabled === true) : false;
    }, [textNotificationData, currentUser?.notificationSettings]);

    // "Latest Activity" popover — lazy-fetched when the dropdown opens and cursor-paginated, so
    // older activity loads as you scroll inside it (same as wal-frontend-v2's radar rows).
    //
    // entityId MUST be masterOrderAutoIncrementId (the master order's id), not data.id — a radar row
    // is a LEG, and its own id has no activity feed. This is the id RadarOrderDetail resolves too.
    const {
        activities: orderActivities,
        isLoading: isActivityLoading,
        isLoadingMore: isActivityLoadingMore,
        hasMore: hasMoreActivity,
        loadMore: loadMoreActivity,
    } = useActivityList({ entityType: 'order', entityId: data.masterOrderAutoIncrementId, enabled: isLatestActivityTooltipOpen });

    const activityDisplayItems = useMemo<IActivityDisplayItem[]>(() => mapActivitiesToDisplayItems(orderActivities), [orderActivities]);

    const [isPriorityLoading, setIsPriorityLoading] = useState(false);

    const handleMarkAsPriority = (e: React.MouseEvent) => {
        e.stopPropagation();

        const newPriorityValue = !data.isPriority;
        setIsPriorityLoading(true);

        updateOrderPriority(String(data.masterOrderId), newPriorityValue)
            .then(() => {
                dispatch(updateTrackOrder({ masterOrderFullId: data.masterOrderFullId, data: { isPriority: newPriorityValue } }));
                WalToast.success(newPriorityValue ? (t('Radar.Marked_as_priority') as string) : (t('Radar.Marked_as_normal') as string));
            })
            .catch((err: any) => {
                logError(err, {
                    errorMessage: err?.message || 'Error updating order priority',
                    errorLocation: 'TrackTableRow: handleMarkAsPriority',
                    storeInDatabase: true,
                    logToConsole: true,
                });
                WalToast.error(t('Radar.Priority_update_failed') as string);
            })
            .finally(() => {
                setIsPriorityLoading(false);
            });
    };

    const handleBellClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsNotificationModalOpen(true);
    };

    // Send to (this load only) prefill: this user's saved per-load addresses, else their profile (cp-meeting-changes F2).
    const notificationDelivery = useMemo(() => getDeliveryDefaults((textNotificationData as any)?.notificationTypes, currentUser), [textNotificationData, currentUser]);

    const handleTextNotificationSave = async (notificationTypes: INotificationTypes, delivery?: IDeliveryPayload) => {
        const response = await saveOrderNotificationSettings(String(data.masterOrderId), notificationTypes, delivery);
        const saved = response?.data?.data?.metadata ?? response?.data?.metadata ?? null;
        setTextNotificationData(saved);
        dispatch(updateTrackOrder({ masterOrderFullId: data.masterOrderFullId, data: { textNotification: saved } }));
    };

    // Six schedule columns (Pickup|Delivery Date / Type / Time) — same derivation as WAL Radar's row.
    const pickupColumns = useMemo(() => getScheduleColumnValues(data.schedule, 'pickup', t), [data.schedule, t]);
    const deliveryColumns = useMemo(() => getScheduleColumnValues(data.schedule, 'delivery', t), [data.schedule, t]);
    const pickupDateRef = useRef<HTMLParagraphElement>(null);
    const pickupTimeRef = useRef<HTMLParagraphElement>(null);
    const deliveryDateRef = useRef<HTMLParagraphElement>(null);
    const deliveryTimeRef = useRef<HTMLParagraphElement>(null);

    // Carrier details tooltip data — mirrors wal-frontend-v2's Radar TableViewRow derivations
    // (contact name/phone are not in the customer portal API, so those rows are omitted).
    // carrier.type can be null in the list response — fall back to the isPrivateCarrier flag.
    const isCommonCarrier = data.carrier?.type ? isCheckCommonCarrier(data.carrier.type) : data.carrier ? !data.carrier.isPrivateCarrier : false;
    const carrierTypeValue = isCommonCarrier ? t('Common.Common') : t('Common.Private');
    const carrierTrackingValue = isCommonCarrier ? (data.trackingId && data.carrierOrderId ? data.trackingId : undefined) : data.trackingLink || undefined;
    const carrierTrackingLabel = isCommonCarrier ? t('Radar.Tracking_ID') : t('Radar.Tracking_Link');

    // Carrier-specific tracking-id name(s) (e.g. "PRO #") — common carriers only; prefixes the tracking id value.
    const carrierTrackingIdNames = data.carrier?.slug ? TRACK_CARRIER_TRACKING_ID_NAMES[data.carrier.slug] ?? [] : [];
    const carrierTrackingIdNamesString = carrierTrackingIdNames.length > 0 ? carrierTrackingIdNames.join(' / ') : '';

    const shipperLabel = `${data.originAddress?.companyName ?? ''}${data.originAddress?.city ? `, ${formatAddress(data.originAddress.city)}` : ''}${
        data.originAddress?.stateCode ? `, ${data.originAddress.stateCode}` : ''
    }${data.originAddress?.postal ? `, ${formatAddress(data.originAddress.postal)}` : ''}`;
    const consigneeLabel = `${data.destinationAddress?.companyName ?? ''}${data.destinationAddress?.city ? `, ${formatAddress(data.destinationAddress.city)}` : ''}${
        data.destinationAddress?.stateCode ? `, ${data.destinationAddress.stateCode}` : ''
    }${data.destinationAddress?.postal ? `, ${formatAddress(data.destinationAddress.postal)}` : ''}`;

    const tableCells = useMemo(
        () => [
            {
                key: 'orderId',
                htmlDynamic: (
                    <td className="max-w-[170px] min-w-[170px] table-cell-with-tooltip">
                        <div className={`flex items-center gap-x-1.5 min-w-0 h-full table-col-tooltip-space-common-xs ${isLatestActivityTooltipOpen ? 'bg-utilityGray100' : ''}`}>
                            <div className="text-utilityBlack800 font-semibold truncate cursor-pointer">#{data.masterOrderFullId}</div>
                            <div onClick={(e) => e.stopPropagation()} className="h-full">
                                <TooltipCmp
                                    TooltipCustomClass="tooltipV3-content-wrap"
                                    isArrow={false}
                                    isTooltipInteractive
                                    parentClassName="tooltip-content-with-scroll-wrap w-[400px] space-y-2 text-left"
                                    openOnClick
                                    position="bottom"
                                    onOpenChange={setIsLatestActivityTooltipOpen}
                                    message={
                                        <ActivityDropdownList
                                            items={activityDisplayItems}
                                            isLoading={isActivityLoading}
                                            isLoadingMore={isActivityLoadingMore}
                                            hasMore={hasMoreActivity}
                                            onLoadMore={loadMoreActivity}
                                            listTitle="Latest Activity"
                                            emptyStateDesc="There is no activity recorded for this order yet."
                                        />
                                    }
                                >
                                    <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full p-1 ${isLatestActivityTooltipOpen ? '!opacity-100' : ''}`}>
                                        <ChevronDown className="w-4 h-4 text-grayLight600" />
                                    </div>
                                </TooltipCmp>
                            </div>
                        </div>
                    </td>
                ),
            },
            {
                key: 'carrierName',
                htmlDynamic: (
                    <td className="min-w-[175px] max-w-[175px] table-cell-with-tooltip">
                        {data.carrier ? (
                            <TooltipCmp
                                TooltipCustomClass="tooltipV3-content-wrap"
                                isArrow={false}
                                position="bottom"
                                openOnClick
                                isTooltipInteractive
                                parentClassName="tooltip-content-with-scroll-wrap space-y-3 min-w-[300px] max-w-[400px] text-left"
                                onOpenChange={setIsCarrierTooltipOpen}
                                message={
                                    <CarrierDetailTooltip
                                        imageUrl={data.carrier?.image}
                                        name={carrierName || (t('Common.Unassigned') as string)}
                                        contactLine={isOrgCarrier && extractMcNumber(orgBranding.mcNumber) ? `MC #${extractMcNumber(orgBranding.mcNumber)}` : undefined}
                                        imageFallbackNode={
                                            isOrgCarrier ? (
                                                <div className="table-profile-customer-circle-xs rounded-md !bg-utilityBlack flex items-center justify-center p-0.5 flex-none">
                                                    <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                                                </div>
                                            ) : undefined
                                        }
                                        details={
                                            isOrgCarrier
                                                ? []
                                                : [
                                                      { label: t('Common.Carrier_Type'), value: carrierTypeValue },
                                                      ...(carrierTrackingValue
                                                          ? [
                                                                {
                                                                    label: carrierTrackingLabel,
                                                                    value: carrierTrackingValue,
                                                                    // Copy icon copies the raw tracking value — tracking id for common carriers,
                                                                    // tracking url for private carriers (the carrier-name prefix is never copied).
                                                                    copyValue: carrierTrackingValue,
                                                                    // Common carriers prefix the tracking id with the carrier-specific name
                                                                    // (e.g. "PRO #: 532861560"). The italic name is shown only when a mapping
                                                                    // exists; otherwise falls back to plain value.
                                                                    ...(isCommonCarrier && carrierTrackingIdNamesString
                                                                        ? {
                                                                              valueNode: (
                                                                                  <span className="inline-flex items-center justify-end gap-x-1.5 min-w-0">
                                                                                      <span className="font-normal italic flex-none">{`${carrierTrackingIdNamesString}:`}</span>
                                                                                      <span className="font-medium truncate">{carrierTrackingValue}</span>
                                                                                  </span>
                                                                              ),
                                                                          }
                                                                        : {}),
                                                                },
                                                            ]
                                                          : []),
                                                  ]
                                        }
                                    />
                                }
                            >
                                <div className={`flex items-center gap-x-2 h-full table-cell-clickable table-col-tooltip-space-common-xs ${isCarrierTooltipOpen ? '!bg-utilityGray200' : ''}`}>
                                    <p className="font-medium text-xs truncate">{carrierName || '-'}</p>
                                    <div className={`table-icon-wrap flex-none ${isCarrierTooltipOpen ? '!opacity-100' : ''}`}>
                                        <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
                                    </div>
                                </div>
                            </TooltipCmp>
                        ) : (
                            <div className="flex items-center gap-x-2 h-full table-col-tooltip-space-common-xs">
                                <p className="font-medium text-xs truncate">{carrierName || '-'}</p>
                            </div>
                        )}
                    </td>
                ),
            },
            {
                key: 'orderType',
                htmlDynamic: (
                    <td className="min-w-[130px] max-w-[130px]">
                        {data.orderType && (
                            <BadgeCmp style="modern" type={getOrderTypeBadge(data.orderType)}>
                                {data.orderType.toUpperCase()}
                            </BadgeCmp>
                        )}
                    </td>
                ),
            },
            {
                key: 'orderStatus',
                htmlDynamic: (
                    <td className="min-w-[140px] max-w-[140px]">
                        {data.orderStatus && (
                            <BadgeCmp style="modern" type={getBadgeType(data.orderStatus)}>
                                {data.orderStatus.charAt(0).toUpperCase() + data.orderStatus.slice(1).replace(/-/g, ' ')}
                            </BadgeCmp>
                        )}
                    </td>
                ),
            },
            // ── Schedule columns — cell markup mirrors WAL Radar TableViewRow (pickupDate/pickupType/…) ──
            // Date carries <sup> ordinal HTML from the shared formatter; the plain text is the tooltip.
            // The note icon appears only when the stop has instructions, with the text in its tooltip.
            {
                key: 'pickupDate',
                htmlDynamic: (
                    <td className="min-w-[150px] max-w-[150px] w-[150px]">
                        <div className="flex items-center gap-x-1 overflow-hidden">
                            <TruncateTextCmp refName={pickupDateRef} message={pickupColumns.datePlain}>
                                <p ref={pickupDateRef} className="truncate" dangerouslySetInnerHTML={{ __html: pickupColumns.date }} />
                            </TruncateTextCmp>
                            {data.pickupInstruction && (
                                <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                                    <TooltipCmp
                                        TooltipCustomClass="tooltipV3-content-wrap"
                                        parentClassName="tooltip-content-with-scroll-wrap w-[320px] space-y-1 text-left"
                                        childrenClassName="flex"
                                        message={
                                            <div className="space-y-1 text-left">
                                                <p className="v3-dropdown-heading">{t('Radar.Pickup_Instructions')}</p>
                                                <p className="text-xs font-normal text-grayLight600">{data.pickupInstruction}</p>
                                            </div>
                                        }
                                    >
                                        <File05 className="w-4 h-4 text-grayLight600 flex-none cursor-pointer -scale-x-100 rotate-180" />
                                    </TooltipCmp>
                                </div>
                            )}
                        </div>
                    </td>
                ),
            },
            {
                key: 'pickupType',
                htmlDynamic: (
                    <td className="min-w-[130px] max-w-[130px] w-[130px]">
                        <p className="truncate">{pickupColumns.type}</p>
                    </td>
                ),
            },
            {
                key: 'pickupTime',
                htmlDynamic: (
                    <td className="min-w-[120px] max-w-[120px] w-[120px]">
                        <TruncateTextCmp refName={pickupTimeRef} message={pickupColumns.time}>
                            <p ref={pickupTimeRef} className="truncate">
                                {pickupColumns.time}
                            </p>
                        </TruncateTextCmp>
                    </td>
                ),
            },
            {
                key: 'deliveryDate',
                htmlDynamic: (
                    <td className="min-w-[150px] max-w-[150px] w-[150px]">
                        <div className="flex items-center gap-x-1 overflow-hidden">
                            <TruncateTextCmp refName={deliveryDateRef} message={deliveryColumns.datePlain}>
                                <p ref={deliveryDateRef} className="truncate" dangerouslySetInnerHTML={{ __html: deliveryColumns.date }} />
                            </TruncateTextCmp>
                            {data.deliveryInstruction && (
                                <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                                    <TooltipCmp
                                        TooltipCustomClass="tooltipV3-content-wrap"
                                        parentClassName="tooltip-content-with-scroll-wrap w-[320px] space-y-1 text-left"
                                        childrenClassName="flex"
                                        message={
                                            <div className="space-y-1 text-left">
                                                <p className="v3-dropdown-heading">{t('Radar.Delivery_Instructions')}</p>
                                                <p className="text-xs font-normal text-grayLight600">{data.deliveryInstruction}</p>
                                            </div>
                                        }
                                    >
                                        <File05 className="w-4 h-4 text-grayLight600 flex-none cursor-pointer -scale-x-100 rotate-180" />
                                    </TooltipCmp>
                                </div>
                            )}
                        </div>
                    </td>
                ),
            },
            {
                key: 'deliveryType',
                htmlDynamic: (
                    <td className="min-w-[130px] max-w-[130px] w-[130px]">
                        <p className="truncate">{deliveryColumns.type}</p>
                    </td>
                ),
            },
            {
                key: 'deliveryTime',
                htmlDynamic: (
                    <td className="min-w-[120px] max-w-[120px] w-[120px]">
                        <TruncateTextCmp refName={deliveryTimeRef} message={deliveryColumns.time}>
                            <p ref={deliveryTimeRef} className="truncate">
                                {deliveryColumns.time}
                            </p>
                        </TruncateTextCmp>
                    </td>
                ),
            },
            {
                key: 'shipperCity',
                htmlDynamic: (
                    <td className="min-w-[185px] max-w-[185px] table-cell-with-tooltip">
                        <TooltipCmp
                            TooltipCustomClass="tooltipV3-content-wrap"
                            isArrow={false}
                            position="bottom"
                            openOnClick
                            isTooltipInteractive
                            parentClassName="tooltip-content-with-scroll-wrap min-w-[220px] text-left space-y-1"
                            onOpenChange={setIsFromTooltipOpen}
                            message={
                                <>
                                    {data.originAddress?.companyName && <h6 className="text-grayLight900 text-xs leading-[1.5] font-medium truncate">{data.originAddress.companyName}</h6>}
                                    <div className="tooltip-content-with-scroll flex justify-between gap-x-2 min-w-0">
                                        <p className="text-xs leading-[1.5] font-medium text-gray500 line-clamp-2 break-words">{data.originAddress?.fullAddress || '-'}</p>
                                        <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={t('Common.Copy')}>
                                            <Copy06
                                                className="w-4 h-4 text-primary cursor-pointer flex-none"
                                                onClick={(e: any) => {
                                                    e.stopPropagation();
                                                    copyToClipboard(data.originAddress?.fullAddress);
                                                    WalToast.copy(t('Common.Copied_to_clipboard') as string, '', 'copy-msg');
                                                }}
                                            />
                                        </TooltipCmp>
                                    </div>
                                </>
                            }
                        >
                            <div className={`flex items-center gap-x-2 h-full table-cell-clickable table-col-tooltip-space-common-xs ${isFromTooltipOpen ? '!bg-utilityGray200' : ''}`}>
                                <p className="font-medium truncate">{shipperLabel}</p>
                                <div className={`table-icon-wrap flex-none ${isFromTooltipOpen ? '!opacity-100' : ''}`}>
                                    <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
                                </div>
                            </div>
                        </TooltipCmp>
                    </td>
                ),
            },
            {
                key: 'consigneeCity',
                htmlDynamic: (
                    <td className="min-w-[185px] max-w-[185px] table-cell-with-tooltip">
                        <TooltipCmp
                            TooltipCustomClass="tooltipV3-content-wrap"
                            isArrow={false}
                            position="bottom"
                            openOnClick
                            isTooltipInteractive
                            parentClassName="tooltip-content-with-scroll-wrap min-w-[220px] text-left space-y-1"
                            onOpenChange={setIsToTooltipOpen}
                            message={
                                <>
                                    {data.destinationAddress?.companyName && <h6 className="text-grayLight900 text-xs leading-[1.5] font-medium truncate">{data.destinationAddress.companyName}</h6>}
                                    <div className="tooltip-content-with-scroll flex justify-between gap-x-2 min-w-0">
                                        <p className="text-xs leading-[1.5] font-medium text-gray500 line-clamp-2 break-words">{data.destinationAddress?.fullAddress || '-'}</p>
                                        <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={t('Common.Copy')}>
                                            <Copy06
                                                className="w-4 h-4 text-primary cursor-pointer flex-none"
                                                onClick={(e: any) => {
                                                    e.stopPropagation();
                                                    copyToClipboard(data.destinationAddress?.fullAddress);
                                                    WalToast.copy(t('Common.Copied_to_clipboard') as string, '', 'copy-msg');
                                                }}
                                            />
                                        </TooltipCmp>
                                    </div>
                                </>
                            }
                        >
                            <div className={`flex items-center gap-x-1 h-full table-cell-clickable table-col-tooltip-space-common-xs ${isToTooltipOpen ? '!bg-utilityGray200' : ''}`}>
                                <span className="font-medium truncate">{consigneeLabel}</span>
                                <div className={`table-icon-wrap flex-none ${isToTooltipOpen ? '!opacity-100' : ''}`}>
                                    <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
                                </div>
                            </div>
                        </TooltipCmp>
                    </td>
                ),
            },
            {
                key: 'action',
                htmlDynamic: (
                    <td className={`table-cell-with-tooltip sticky right-0 bg-inherit z-[1] ${actionColumnWidthClass || 'min-w-[64px] max-w-[64px] w-[64px]'}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-x-2 flex-none table-col-tooltip-space-common-xs ml-auto h-full justify-end">
                            <TooltipCmp message={data.isPriority ? t('Radar.Mark_as_Normal') : t('Radar.Mark_as_Priority')} childrenClassName="flex">
                                <ButtonCmp
                                    className={!isPriorityLoading ? 'hover:text-primary' : ''}
                                    size="none"
                                    icon={
                                        isPriorityLoading ? (
                                            <div className="animate-spin rounded-full w-4 h-4 border-2 border-primary border-t-transparent" />
                                        ) : data.isPriority ? (
                                            <Lightning01 className="w-4 h-4 flex-none fill-current" />
                                        ) : (
                                            <Lightning01 className="w-4 h-4 flex-none" />
                                        )
                                    }
                                    onClick={handleMarkAsPriority}
                                    disabled={isPriorityLoading}
                                >
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>
                            {isTextNotificationFeatureVisible && (
                                <TooltipCmp message={t('Dashboard.NotificationSettings.Title')} childrenClassName="flex">
                                    <ButtonCmp
                                        className="hover:text-primary"
                                        size="none"
                                        icon={isNotificationEnabled ? <Bell01 className="w-4 h-4 flex-none text-[#3538CD]" /> : <BellOff01 className="w-4 h-4 flex-none" />}
                                        onClick={handleBellClick}
                                    >
                                        <></>
                                    </ButtonCmp>
                                </TooltipCmp>
                            )}
                        </div>
                    </td>
                ),
            },
        ],
        // The activity values MUST stay in this list: the dropdown's contents live inside this
        // memoized JSX, so omitting them freezes the cell at its first render (empty list,
        // isLoading=false) and the popover permanently reads "No Latest Activity" even after the
        // fetch resolves. The map card renders in the component body and so never had this issue.
        [
            data,
            shipperLabel,
            consigneeLabel,
            carrierName,
            isOrgCarrier,
            orgBranding,
            pickupColumns,
            deliveryColumns,
            isFromTooltipOpen,
            isToTooltipOpen,
            isCarrierTooltipOpen,
            isNotificationEnabled,
            isTextNotificationFeatureVisible,
            isPriorityLoading,
            actionColumnWidthClass,
            t,
            isLatestActivityTooltipOpen,
            activityDisplayItems,
            isActivityLoading,
            isActivityLoadingMore,
            hasMoreActivity,
            loadMoreActivity,
        ]
    );

    return (
        <>
            {headCells
                ?.filter((cell: any) => cell.visible)
                .map((cell: any) => {
                    if (cell.id === 'checkbox') {
                        return (
                            <React.Fragment key="checkbox">
                                <td className="w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1] table-cell-with-tooltip" onClick={(e) => e.stopPropagation()}>
                                    <CheckBox
                                        id={`select-track-${data.legDbId}`}
                                        checked={selectedTrackIds?.includes(data.legDbId) ?? false}
                                        onChangeFunc={(e: any) => {
                                            e.stopPropagation();
                                            onToggleSelect?.(data.legDbId);
                                        }}
                                        name="selectTrack"
                                        value={String(data.legDbId)}
                                        parentClassName="table-col-tooltip-space-common-xs h-full items-center"
                                    />
                                </td>
                            </React.Fragment>
                        );
                    }

                    const matched = tableCells.find((tCell) => tCell.key === cell.id);

                    if (!matched) return null;

                    return <React.Fragment key={cell.id}>{matched.htmlDynamic}</React.Fragment>;
                })}

            {isNotificationModalOpen && (
                <NotificationSettingsModal
                    handleClose={() => setIsNotificationModalOpen(false)}
                    existingData={notificationExistingData}
                    onSave={handleTextNotificationSave}
                    delivery={notificationDelivery}
                />
            )}
        </>
    );
};

export default TrackTableRow;
